import type { FinanceAnalyticsByMonthsResponseDto } from '@/features/finances/types/finances-dto';

/** Keys aligned with API / backend RentAnalyticsExpenseVisibility */
export const RENT_ANALYTICS_EXPENSE_LINE_KEYS = [
    'commissions',
    'incomeTax',
    'internet',
    'water',
    'electricity',
    'gas',
    'previousMonthUtilities',
] as const;

export type RentAnalyticsExpenseLineKey = (typeof RENT_ANALYTICS_EXPENSE_LINE_KEYS)[number];

export const rentAnalyticsExpenseLineChartColor: Record<RentAnalyticsExpenseLineKey, string> = {
    commissions: '#D43FBB',
    incomeTax: '#9B1CE0',
    internet: '#565DCF',
    water: '#2EB566',
    electricity: '#E6A82A',
    gas: '#E6782A',
    previousMonthUtilities: '#2EC8F2',
};

export const rentAnalyticsExpenseLineTranslationKey: Record<
    RentAnalyticsExpenseLineKey,
    string
> = {
    commissions: 'main.finances.components.analytics.components.expenses.commissions',
    incomeTax: 'main.finances.components.analytics.components.expenses.incomeTax',
    internet: 'main.finances.components.analytics.components.expenses.internet',
    water: 'main.finances.components.analytics.components.expenses.water',
    electricity: 'main.finances.components.analytics.components.expenses.electricity',
    gas: 'main.finances.components.analytics.components.expenses.gas',
    previousMonthUtilities:
        'main.finances.components.analytics.components.expenses.previousMonthUtilities',
};

export function expenseLineValue(
    data: FinanceAnalyticsByMonthsResponseDto | undefined,
    key: RentAnalyticsExpenseLineKey,
): number {
    switch (key) {
        case 'commissions':
            return data?.commissions ?? 0;
        case 'incomeTax':
            return data?.incomeTax ?? 0;
        case 'internet':
            return data?.internet ?? 0;
        case 'water':
            return data?.water ?? 0;
        case 'electricity':
            return data?.electricity ?? 0;
        case 'gas':
            return data?.gas ?? 0;
        case 'previousMonthUtilities':
            return data?.previousMonthUtilities ?? 0;
        default:
            return 0;
    }
}

export function buildRentAnalyticsExpenseChartData(
    data: FinanceAnalyticsByMonthsResponseDto | undefined,
    hiddenExpenseKeySet: ReadonlySet<string>,
): { value: number; color: string }[] {
    return RENT_ANALYTICS_EXPENSE_LINE_KEYS.filter(
        (key) => !hiddenExpenseKeySet.has(key.toLowerCase()),
    ).map((key) => ({
        value: expenseLineValue(data, key),
        color: rentAnalyticsExpenseLineChartColor[key],
    }));
}

export function areAllRentAnalyticsExpenseLinesHidden(
    hiddenExpenseKeySet: ReadonlySet<string>,
): boolean {
    return RENT_ANALYTICS_EXPENSE_LINE_KEYS.every((key) =>
        hiddenExpenseKeySet.has(key.toLowerCase()),
    );
}
